import { Middleware } from '@reduxjs/toolkit';

import {
  setDbMetadata,
  setTemp,
  createWorker,
  createPassword,
  fetchWorkerDetails,
  updateWorker,
  uploadGovernmentDocumentFiles,
  deleteGovernmentDocumentFile,
  fetchCertificationGroups,
  fetchAllCertifications,
  fetchWorkerExistingConsentForms,
  addCertification,
  updateCertification,
  deleteCertification,
  setOnboarding,
  fetchAssignedCompanies,
  submitConsentAcceptances,
  fetchWorkerConsentAcceptances,
} from './onboarding';
import { createOrUpdateWorker, getWorker } from 'utils/indexedDB';
import { getSectionsCompleted } from 'modules/views/shared/Wizard/utils';
import { OnboardingStatus } from 'models/general';

// List of action types that indicate data has changed and should be persisted
const SAVE_TRIGGERS = [
  createWorker.fulfilled.type,
  createPassword.fulfilled.type,
  fetchWorkerDetails.fulfilled.type,
  updateWorker.fulfilled.type,
  uploadGovernmentDocumentFiles.fulfilled.type,
  deleteGovernmentDocumentFile.fulfilled.type,
  fetchCertificationGroups.fulfilled.type,
  fetchAllCertifications.fulfilled.type,
  fetchWorkerExistingConsentForms.fulfilled.type,
  addCertification.fulfilled.type,
  updateCertification.fulfilled.type,
  deleteCertification.fulfilled.type,
  fetchAssignedCompanies.fulfilled.type,
  submitConsentAcceptances.fulfilled.type,
  fetchWorkerConsentAcceptances.fulfilled.type,
  setTemp.type,
  setOnboarding.type,
];

export const indexedDBSyncMiddleware: Middleware = store => next => async action => {
  // We let the action finish so 'store.getState()' returns the FINAL updated data.
  const result = next(action);

  const state = store.getState();
  const onboarding = state?.onboarding;
  const user = state?.user;
  const cache = state?.cache;

  // CHECK IF WE SHOULD AUTO-SAVE
  if (
    SAVE_TRIGGERS.includes(action.type) &&
    (action.type === setTemp.type ? onboarding.id : true) &&
    user?.userId &&
    !user?.isWorker
  ) {
    // We need Project ID to save meaningful data
    const projectId = onboarding.db?.projectId || onboarding.temp?.project?.value;

    if (!projectId || !(onboarding.firstName || onboarding.mobilePhoneNumber || onboarding.email)) {
      return result;
    }

    // Never write over a worker the upload manager is actively syncing. `createOrUpdateWorker` does a
    // full-record `put`, so saving here mid-upload would revert everything the step handlers have
    // already persisted — cleared pendingUploads flags, the server-assigned worker id, the resolved
    // signature URL — and cause previously-uploaded data to be submitted a second time.
    if (onboarding.db?.id) {
      const inFlight = await getWorker(onboarding.db.id);
      if (inFlight?.uploadingDetails?.status === OnboardingStatus.PROCESSING) {
        return result;
      }
    }

    try {
      // SAVE TO indexedDB
      const completedSections = getSectionsCompleted({
        onboarding,
        profileFieldConstraintsMap: cache.profileFieldConstraintsMap,
        canCreateUnverifiedWorkers: cache.canCreateUnverifiedWorkers,
      });
      let workerStatus = onboarding.uploadingDetails?.status;
      if (!onboarding?.uploadingDetails?.errors?.pageUrl) {
        if (
          onboarding.uploadingDetails?.isOffline ||
          Object.values(onboarding.uploadingDetails?.pendingUploads || {}).some(Boolean)
        ) {
          workerStatus = OnboardingStatus.PENDING;
        } else if (completedSections?.consentForm) {
          workerStatus = OnboardingStatus.COMPLETED;
        }
      }
      let workerToSave = {
        ...onboarding,
        lastUpdatedAt:
          !onboarding.lastUpdatedAt || onboarding.uploadingDetails?.isOffline
            ? new Date().toISOString()
            : onboarding.lastUpdatedAt,
        uploadingDetails: {
          ...(onboarding.uploadingDetails || {}),
          status: workerStatus,
          sectionsCompleted: completedSections,
        },
        db: {
          ...(onboarding.db || {}),
          createdByCompanyId: onboarding.db?.createdByCompanyId || user.currentCompanyId,
        },
      };
      const savedWorker = await createOrUpdateWorker(workerToSave);

      // FEEDBACK LOOP (Hydrate Redux)
      // If IndexedDB generated a new db.id (e.g., this was the first save),
      // we must update Redux so the next save uses the correct ID.
      const currentDbId = onboarding.db?.id;
      const newDbId = savedWorker.db?.id;

      if (newDbId && currentDbId !== newDbId) {
        // Dispatch the metadata back to Redux
        store.dispatch(setDbMetadata(savedWorker.db));
      }
    } catch (err) {
      console.error('❌ Failed to Auto-Persist Worker:', err);
    }
  }

  return result;
};
